import {chartMaxK, poissonProbability} from '../poissonRain/rainModel';

/**
 * The story behind the cookie oven: chocolate chips land on a cookie as a
 * Poisson process over its area, so the chips in a whole cookie are
 * Poisson(θ) and the chips in half of it are Poisson(θ/2). Kept free of
 * React so it can be tested.
 */

/** Chips per cookie, on average. */
export const COOKIE_THETA = 4;

/** A source of uniform random numbers in [0, 1), like `Math.random`. */
export type Random = () => number;

/**
 * Draws how many chips a cookie gets: Poisson(`theta`), by Knuth's method
 * (multiply uniforms until the product drops below e^−θ).
 *
 * @param params.theta - Chips per cookie, on average.
 * @param params.random - Uniform random numbers in [0, 1).
 * @return A whole number of chips, at least 0.
 * @throws Never.
 */
export function sampleChipCount({
  theta,
  random,
}: {
  theta: number;
  random: Random;
}): number {
  const floor = Math.exp(-theta);
  let product = random();
  let count = 0;
  while (product >= floor) {
    count += 1;
    product *= random();
  }
  return count;
}

/** Where a chip sits on the cookie, relative to its centre, in [−1, 1]². */
export interface ChipSpot {
  x: number;
  y: number;
}

/**
 * Scatters chips uniformly over the cookie: each spot is drawn uniformly in
 * the unit square and kept only if it falls inside the unit disc, so every
 * patch of cookie is equally likely to get it.
 *
 * @param params.count - How many chips.
 * @param params.random - Uniform random numbers in [0, 1).
 * @return One spot per chip.
 * @throws Never.
 */
export function chipSpots({
  count,
  random,
}: {
  count: number;
  random: Random;
}): ChipSpot[] {
  const spots: ChipSpot[] = [];
  while (spots.length < count) {
    const x = 2 * random() - 1;
    const y = 2 * random() - 1;
    if (x * x + y * y <= 0.92 * 0.92) {
      spots.push({x, y});
    }
  }
  return spots;
}

/**
 * How many of a cookie's chips the right half holds; a bite takes that
 * half. Chips on the dividing line count as the left half's.
 *
 * @param spots - The cookie's chips.
 * @return The chips in the right half.
 * @throws Never.
 */
export function chipsInRightHalf(spots: readonly ChipSpot[]): number {
  return spots.filter(spot => spot.x > 0).length;
}

/** One bar of the oven's chart and the Poisson prediction beside it. */
export interface CookieBin {
  /** Chips in a cookie. */
  k: number;
  /** The share of the cookies baked so far with exactly k chips. */
  observed: number;
  /** What Poisson(θ) predicts for that share. */
  expected: number;
}

/**
 * The chart: for each count k from 0 to about four standard deviations
 * above θ, the share of baked cookies with exactly k chips, beside the
 * Poisson prediction.
 *
 * @param params.cookies - Chip counts of the cookies baked so far.
 * @param params.theta - Chips per cookie, on average.
 * @return One bin per k; cookies with more chips than the last bin count
 *     toward no bar.
 * @throws Never.
 */
export function cookieChart({
  cookies,
  theta,
}: {
  cookies: readonly number[];
  theta: number;
}): CookieBin[] {
  const maxK = chartMaxK(theta);
  return Array.from({length: maxK + 1}, (_, k) => ({
    k,
    observed:
      cookies.length === 0
        ? 0
        : cookies.filter(count => count === k).length / cookies.length,
    expected: poissonProbability({k, rate: theta}),
  }));
}

/**
 * How many cookies, on average, come out between plain ones: 1 / P(0).
 *
 * @param theta - Chips per cookie, on average.
 * @return Cookies per plain cookie, rounded to a whole number.
 * @throws Never.
 */
export function cookiesPerPlainOne(theta: number): number {
  return Math.round(Math.exp(theta));
}
